import { useCallback, useEffect, useState } from "react";
import AdminHeader from "../components/admin/AdminHeader";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, Loader2, Save, Stethoscope,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { adminApi, useAdminAuth } from "../lib/adminAuth";
import FdaLabelPanel from "../components/admin/FdaLabelPanel";
import SourceChips from "../components/admin/SourceChips";

function GuidanceField({ title, hint, value, sources, testId, active, onFocus, onChange }) {
  return (
    <div
      onFocusCapture={onFocus}
      className={`rounded-xl border p-3 transition ${active ? "border-stone-900" : "border-stone-200 bg-white"}`}
      data-testid={`guidance-field-${testId}`}
    >
      <div className="flex items-center justify-between">
        <b className="text-sm text-stone-900">{title}</b>
        {active && <span className="text-[10px] uppercase tracking-wider text-emerald-700">active</span>}
      </div>
      <p className="mt-0.5 text-[12px] text-stone-500">{hint}</p>
      <Textarea
        value={value} rows={3} maxLength={1000}
        onChange={(e) => onChange({ body: e.target.value })}
        className="mt-2 text-sm" data-testid={`guidance-body-${testId}`}
      />
      <div className="mt-2">
        <SourceChips
          sources={sources || []}
          testid={`guidance-sources-${testId}`}
          onChange={(srcs) => onChange({ sources: srcs })}
        />
      </div>
    </div>
  );
}

export default function AdminMedicationGuidance() {
  const { admin } = useAdminAuth();
  const canReview = admin?.role === "clinician";
  const [meds, setMeds] = useState([]);
  const [medId, setMedId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeField, setActiveField] = useState("watch_for");

  useEffect(() => {
    adminApi().get("/admin/med-guidance/medications")
      .then(({ data: d }) => setMeds(d.items))
      .catch(() => toast.error("Failed to load medications"));
  }, []);

  const loadMed = useCallback(async (id) => {
    if (!id) { setData(null); return; }
    setLoading(true);
    try {
      const { data: d } = await adminApi().get(`/admin/med-guidance/${id}`);
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load guidance");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadMed(medId); }, [medId, loadMed]);

  function patch(field, updates) {
    setData((prev) => {
      const next = { ...prev };
      if ("body" in updates) next[field] = updates.body;
      if ("sources" in updates) next[`${field}_sources`] = updates.sources;
      return next;
    });
  }

  function fillSource(src) {
    const key = `${activeField}_sources`;
    const cur = data?.[key] || [];
    if (cur.some((x) => x.url === src.url)) return false;
    setData((prev) => ({ ...prev, [key]: [...(prev[key] || []), { url: src.url, label: src.label }] }));
    return true;
  }

  async function save() {
    setSaving(true);
    try {
      const { data: d } = await adminApi().put(`/admin/med-guidance/${medId}`, {
        watch_for: data.watch_for || null,
        watch_for_sources: data.watch_for_sources || [],
        restart_guidance: data.restart_guidance || null,
        restart_guidance_sources: data.restart_guidance_sources || [],
      });
      setData(d);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  const medName = meds.find((m) => m.id === medId)?.label || data?.medication_name;

  return (
    <main data-testid="admin-med-guidance-page" className="min-h-screen bg-[#fdf8f3] px-6 py-10">
      <div className="mx-auto max-w-5xl">
        <AdminHeader />

        <div className="flex items-center gap-2">
          <Stethoscope className="h-6 w-6 text-stone-700" />
          <h1 className="text-3xl font-semibold tracking-tight text-stone-900">Medication notes (watch-for &amp; restart)</h1>
        </div>
        <p className="mt-1 text-sm text-stone-600">
          Feeds page 2 of the Discussion Report.
        </p>

        <div className="mt-5 max-w-sm">
          <label className="text-[11px] font-medium uppercase tracking-wide text-stone-500">Medication</label>
          <Select value={medId} onValueChange={setMedId}>
            <SelectTrigger data-testid="guidance-med-select" className="mt-1 h-9 text-sm">
              <SelectValue placeholder="Choose a medication…" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {meds.map((m) => (
                <SelectItem key={m.id} value={m.id} className="text-sm">{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading && (
          <div className="mt-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-stone-400" /></div>
        )}

        {data && !loading && (
          <div className="mt-5 grid gap-4 lg:grid-cols-[3fr_2fr]">
            <div className="flex flex-col gap-3">
              <GuidanceField
                title="What to watch for" testId="watch"
                hint="Side effects or warning signs the patient should look out for."
                value={data.watch_for || ""} sources={data.watch_for_sources || []}
                active={activeField === "watch_for"}
                onFocus={() => setActiveField("watch_for")}
                onChange={(u) => patch("watch_for", u)}
              />
              <GuidanceField
                title="Restart guidance" testId="restart"
                hint="When it's safe to restart retinoids / actives after this medication."
                value={data.restart_guidance || ""} sources={data.restart_guidance_sources || []}
                active={activeField === "restart_guidance"}
                onFocus={() => setActiveField("restart_guidance")}
                onChange={(u) => patch("restart_guidance", u)}
              />
              <div className="flex items-center gap-2">
                <Button className="self-start" onClick={save} disabled={saving || !canReview}
                  title={!canReview ? "Clinical reviewer only." : undefined} data-testid="guidance-save">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  <span className="ml-1">Save</span>
                </Button>
                {!canReview && (
                  <span data-testid="guidance-clinician-only"
                    className="text-[11px] font-semibold uppercase tracking-wider text-stone-500">
                    Clinical reviewer only
                  </span>
                )}
              </div>
            </div>
            <div>
              <FdaLabelPanel
                medicationId={medId} medicationName={medName}
                useHint={activeField === "watch_for" ? "What to watch for" : "Restart guidance"}
                onUse={fillSource}
              />
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
